import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckSquare, Check, X, Loader2, LogOut, LayoutDashboard, Users, Monitor, Megaphone, Calendar, BarChart3, CreditCard, Image, FileVideo } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";
import { Textarea } from "../../../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

export default function AdvertisingApprovals() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [artwork, setArtwork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectingId, setRejectingId] = useState(null);

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (user) fetchArtwork(); }, [user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) { navigate("/admin/login"); }
  };

  const fetchArtwork = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/advertising/artwork?status=pending`, { withCredentials: true });
      setArtwork(response.data);
    } catch (error) {
      console.error("Failed to fetch artwork:", error);
    } finally { setLoading(false); }
  };

  const handleApprove = async (artworkId) => {
    try {
      await axios.post(`${API}/advertising/artwork/${artworkId}/approve`, {}, { withCredentials: true });
      fetchArtwork();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to approve artwork");
    }
  };

  const handleReject = async (artworkId) => {
    if (!rejectionReason || rejectionReason.length < 10) {
      alert("Rejection reason must be at least 10 characters");
      return;
    }
    try {
      await axios.post(`${API}/advertising/artwork/${artworkId}/reject`, { reason: rejectionReason }, { withCredentials: true });
      setRejectingId(null);
      setRejectionReason("");
      fetchArtwork();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to reject artwork");
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>);
  }

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800"><ParkWiseLogo size="md" /><p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p></div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (<button key={item.path} onClick={() => navigate(item.path)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}><item.icon className="w-5 h-5" /><span className="text-sm font-medium">{item.label}</span></button>);
          })}
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button onClick={() => navigate("/admin/dashboard")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800"><LayoutDashboard className="w-5 h-5" /><span className="text-sm">← Back to Main Admin</span></button>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2"><ParkWiseLogo size="sm" /><div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-slate-500 truncate">{user?.email}</p></div></div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2"><LogOut className="w-4 h-4" /><span>Sign out</span></button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div><h1 className="text-2xl font-bold text-white">Artwork Approvals</h1><p className="text-slate-400 mt-1">Review and approve campaign artwork</p></div>
            <div className="text-sm text-slate-400"><strong className="text-amber-400">{artwork.length}</strong> pending approvals</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
          ) : artwork.length === 0 ? (
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="py-12 text-center">
                <CheckSquare className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-slate-400">No artwork pending approval. All caught up!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {artwork.map(art => (
                <Card key={art.artwork_id} className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-sm text-white flex items-center gap-2">
                      {art.file_type === 'video' ? <FileVideo className="w-4 h-4" /> : <Image className="w-4 h-4" />}
                      {art.campaign_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="aspect-video bg-slate-800 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                      {art.file_url ? (
                        art.file_type === 'video' ? (
                          <video src={art.file_url} className="w-full h-full object-cover" controls />
                        ) : (
                          <img src={art.file_url} alt="Artwork" className="w-full h-full object-cover" />
                        )
                      ) : (
                        <Image className="w-12 h-12 text-slate-600" />
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mb-4">
                      <p>Resolution: {art.resolution || "N/A"}</p>
                      <p>Duration: {art.duration_seconds}s</p>
                      <p>Uploaded: {new Date(art.uploaded_at).toLocaleDateString()}</p>
                    </div>
                    
                    {rejectingId === art.artwork_id ? (
                      <div className="space-y-2">
                        <Textarea 
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Rejection reason (required, min 10 chars)..."
                          className="bg-slate-800 border-slate-700 text-white text-sm"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="destructive" onClick={() => handleReject(art.artwork_id)} disabled={rejectionReason.length < 10}>Confirm Reject</Button>
                          <Button size="sm" variant="outline" onClick={() => { setRejectingId(null); setRejectionReason(""); }} className="border-slate-700">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleApprove(art.artwork_id)}><Check className="w-4 h-4 mr-1" />Approve</Button>
                        <Button size="sm" variant="destructive" className="flex-1" onClick={() => setRejectingId(art.artwork_id)}><X className="w-4 h-4 mr-1" />Reject</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
